/* eslint-disable react-refresh/only-export-components */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AdminShell } from "@/components/layout/AdminShell";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminSettingsGate } from "@/components/auth/AdminSettingsGate";
import { PeriodFilterProvider } from "@/contexts/PeriodFilterContext";
import { RootRouteComponent } from "@/routes/__root";
import { IndexRouteComponent } from "@/routes/index";
import { LoginRouteComponent } from "@/routes/login";
import { SetPasswordRouteComponent } from "@/routes/set-password";
import { validatePeriodSearch } from "@/routes/periodSearch";
import { validateUsersSearch } from "@/routes/usersSearch";
import { validateInboxSearch } from "@/routes/inboxSearch";

const OverviewRouteComponent = lazyRouteComponent(() => import("@/routes/overview"), "OverviewRouteComponent");
const RevenueRouteComponent = lazyRouteComponent(() => import("@/routes/revenue"), "RevenueRouteComponent");
const AcquisitionRouteComponent = lazyRouteComponent(() => import("@/routes/acquisition"), "AcquisitionRouteComponent");
const RetentionRouteComponent = lazyRouteComponent(() => import("@/routes/retention"), "RetentionRouteComponent");
const EngagementRouteComponent = lazyRouteComponent(() => import("@/routes/engagement"), "EngagementRouteComponent");
const EfficacyRouteComponent = lazyRouteComponent(() => import("@/routes/efficacy"), "EfficacyRouteComponent");
const FeedbackRouteComponent = lazyRouteComponent(() => import("@/routes/feedback"), "FeedbackRouteComponent");
const NpsRouteComponent = lazyRouteComponent(() => import("@/routes/nps"), "NpsRouteComponent");
const UsersListRouteComponent = lazyRouteComponent(() => import("@/routes/users/index"), "UsersListRouteComponent");
const UserProfileRouteComponent = lazyRouteComponent(() => import("@/routes/users/$userId"), "UserProfileRouteComponent");
const PatientsRouteComponent = lazyRouteComponent(() => import("@/routes/patients"), "PatientsRouteComponent");
const BiClinicalRouteComponent = lazyRouteComponent(() => import("@/routes/bi-clinical"), "BiClinicalRouteComponent");
const MarketingRouteComponent = lazyRouteComponent(() => import("@/routes/marketing"), "MarketingRouteComponent");
const SystemRouteComponent = lazyRouteComponent(() => import("@/routes/system"), "SystemRouteComponent");
const AuditRouteComponent = lazyRouteComponent(() => import("@/routes/audit"), "AuditRouteComponent");
const ConfigRouteComponent = lazyRouteComponent(() => import("@/routes/config"), "ConfigRouteComponent");
const WhatsAppConfigRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/config"), "WhatsAppConfigRouteComponent");
const WhatsAppInboxRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/inbox"), "WhatsAppInboxRouteComponent");
const WhatsAppDashboardRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/dashboard"), "WhatsAppDashboardRouteComponent");
const WhatsAppTemplatesRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/templates"), "WhatsAppTemplatesRouteComponent");
const WhatsAppAssetsRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/assets"), "WhatsAppAssetsRouteComponent");
const WhatsAppCampaignsRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/campanhas"), "WhatsAppCampaignsRouteComponent");
const WhatsAppFlowsRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/flows"), "WhatsAppFlowsRouteComponent");
const WhatsAppAgentsRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/agentes"), "WhatsAppAgentsRouteComponent");
const WhatsAppAnchorsRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/anchors"), "WhatsAppAnchorsRouteComponent");
const WhatsAppReviewRouteComponent = lazyRouteComponent(() => import("@/routes/whatsapp/revisao"), "WhatsAppReviewRouteComponent");
const AtendimentoRouteComponent = lazyRouteComponent(() => import("@/routes/atendimento"), "AtendimentoRouteComponent");

const rootRoute = createRootRoute({
  component: RootRouteComponent,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRouteComponent,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  component: LoginRouteComponent,
});

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "set-password",
  component: SetPasswordRouteComponent,
});

function AdminLayoutRouteComponent() {
  return (
    <ProtectedRoute>
      <AdminSettingsGate>
        <PeriodFilterProvider>
          <AdminShell>
            <Outlet />
          </AdminShell>
        </PeriodFilterProvider>
      </AdminSettingsGate>
    </ProtectedRoute>
  );
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AdminLayoutRouteComponent,
});

const overviewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "overview",
  validateSearch: validatePeriodSearch,
  component: OverviewRouteComponent,
});

const revenueRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "revenue",
  validateSearch: validatePeriodSearch,
  component: RevenueRouteComponent,
});

const acquisitionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "acquisition",
  validateSearch: validatePeriodSearch,
  component: AcquisitionRouteComponent,
});

const retentionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "retention",
  validateSearch: validatePeriodSearch,
  component: RetentionRouteComponent,
});

const engagementRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "engagement",
  validateSearch: validatePeriodSearch,
  component: EngagementRouteComponent,
});

const efficacyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "efficacy",
  validateSearch: validatePeriodSearch,
  component: EfficacyRouteComponent,
});

const npsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "nps",
  validateSearch: validatePeriodSearch,
  component: NpsRouteComponent,
});

const feedbackRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "feedback",
  validateSearch: validatePeriodSearch,
  component: FeedbackRouteComponent,
});

const usersIndexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "users",
  validateSearch: validateUsersSearch,
  component: UsersListRouteComponent,
});

const userProfileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "users/$userId",
  validateSearch: validatePeriodSearch,
  component: UserProfileRouteComponent,
});

const biClinicalRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "bi-clinical",
  validateSearch: validatePeriodSearch,
  component: BiClinicalRouteComponent,
});

const patientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "patients",
  validateSearch: validatePeriodSearch,
  component: PatientsRouteComponent,
});

const marketingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "marketing",
  validateSearch: validatePeriodSearch,
  component: MarketingRouteComponent,
});

const systemRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "system",
  validateSearch: validatePeriodSearch,
  component: SystemRouteComponent,
});

const auditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "audit",
  validateSearch: validatePeriodSearch,
  component: AuditRouteComponent,
});

const configRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "config",
  validateSearch: validatePeriodSearch,
  component: ConfigRouteComponent,
});

const whatsappConfigRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/config",
  component: WhatsAppConfigRouteComponent,
});

const whatsappInboxRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/inbox",
  validateSearch: validateInboxSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/atendimento", search });
  },
  component: WhatsAppInboxRouteComponent,
});

const whatsappDashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/dashboard",
  component: WhatsAppDashboardRouteComponent,
});

const whatsappTemplatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/templates",
  component: WhatsAppTemplatesRouteComponent,
});

// WPP-002.08 — Asset library `/whatsapp/assets` (deriva de wpp_templates.components)
const whatsappAssetsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/assets",
  component: WhatsAppAssetsRouteComponent,
});

const whatsappCampaignsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/campanhas",
  component: WhatsAppCampaignsRouteComponent,
});

const whatsappFlowsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/flows",
  component: WhatsAppFlowsRouteComponent,
});

const whatsappAgentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/agentes",
  component: WhatsAppAgentsRouteComponent,
});

// WPP-002.15 — Página de visualização das âncoras pós-tap + toggles operacionais.
const whatsappAnchorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/anchors",
  component: WhatsAppAnchorsRouteComponent,
});

// WPP-001.48 AC15: rota `/whatsapp/revisao` escondida do menu + redireciona
// para o dashboard quando acessada via URL direta. A página existe mas a
// tabela `wpp_ai_review_queue` (esperada como backing store) NÃO EXISTE no
// schema. Plano completo em docs/TECH-DEBT.md TD-WPP-REVISAO. Mantemos o
// componente carregável para facilitar reativação rápida quando a feature
// for implementada (basta remover o beforeLoad).
const whatsappReviewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "whatsapp/revisao",
  beforeLoad: () => {
    throw redirect({ to: "/whatsapp/dashboard" });
  },
  component: WhatsAppReviewRouteComponent,
});

const atendimentoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "atendimento",
  validateSearch: validateInboxSearch,
  component: AtendimentoRouteComponent,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  setPasswordRoute,
  protectedRoute.addChildren([
    overviewRoute,
    revenueRoute,
    acquisitionRoute,
    retentionRoute,
    engagementRoute,
    efficacyRoute,
    feedbackRoute,
    npsRoute,
    usersIndexRoute,
    userProfileRoute,
    patientsRoute,
    biClinicalRoute,
    marketingRoute,
    systemRoute,
    auditRoute,
    configRoute,
    whatsappConfigRoute,
    whatsappInboxRoute,
    whatsappDashboardRoute,
    whatsappTemplatesRoute,
    whatsappAssetsRoute,
    whatsappCampaignsRoute,
    whatsappFlowsRoute,
    whatsappAgentsRoute,
    whatsappAnchorsRoute,
    whatsappReviewRoute,
    atendimentoRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
